/**
 * Mount del chrome militar en una página autenticada.
 *
 * Responsabilidades:
 *  1. Comprobar sesión vía `GET /api/auth/me`. Si no hay → redirige al
 *     CRT de login con `return=...` para volver tras autenticar.
 *  2. Inyecta el chrome (sidebars, header, footer) en `document.body`,
 *     reemplazando cualquier contenido inicial. La página viene a un
 *     `<body>` casi vacío y se pinta dentro del `contentRoot` que devuelve
 *     este mount.
 *  3. Conecta SALIR (cierra sesión y vuelve a `/`).
 *  4. Bloquea clicks en items "coming-soon".
 *
 * Patrón de uso desde una página:
 *   const { contentRoot, user } = await mountChrome({ activeSection: 'reportes' });
 *   contentRoot.innerHTML = renderMyPage(user);
 */

import type { AuthenticatedPilot } from '@51esc/shared';
import * as authApi from '../auth/api.js';
import { renderChrome, type ChromeSection } from './markup.js';

import './chrome.css';

export interface MountChromeOptions {
  /** Sección que se resaltará como activa en el menú lateral. */
  activeSection: ChromeSection;
  /**
   * Si `true`, la página se monta también para usuarios sin sesión —
   * no se redirige a `/login.html` y `result.user` queda como `null`.
   * Útil para páginas públicas (galería, etc.) que siguen queriendo
   * el chrome militar pero no requieren login.
   *
   * El menú lateral omite los items que requieren sesión, el bloque de
   * usuario se sustituye por un link "INICIAR SESIÓN" y SALIR
   * desaparece. La página recibe `user: null` y decide qué affordances
   * mostrar (p.ej. esconder "Subir foto").
   */
  allowAnonymous?: boolean;
}

/**
 * Resultado del mount. `user` es `null` solo si se pasó
 * `allowAnonymous: true` y no había sesión — en cualquier otro caso
 * será un `AuthenticatedPilot` completo.
 */
export interface MountChromeResult {
  /** `<main class="chrome-main">` donde la página debe renderizar. */
  contentRoot: HTMLElement;
  /** Usuario autenticado, o `null` si la página acepta anónimos. */
  user: AuthenticatedPilot | null;
}

/**
 * Variante explícita para páginas que requieren sesión — devuelve
 * `user` no-null. Es lo que devuelven la mayoría de páginas internas
 * cuando llaman a `mountChrome` sin `allowAnonymous`.
 */
export interface MountChromeResultAuthed extends MountChromeResult {
  user: AuthenticatedPilot;
}

/**
 * Monta el chrome y devuelve el slot principal y los datos del usuario.
 *
 * Por defecto, si NO hay sesión, redirige a
 * `/login.html?return=<currentPath>` y devuelve una promesa que nunca
 * resuelve — el caller no debe seguir ejecutando lógica posterior.
 *
 * Con `allowAnonymous: true`, el usuario no autenticado entra a la
 * página y `result.user` queda `null`.
 *
 * Overloads para conservar el tipo: por defecto `user` es no-null,
 * con `allowAnonymous: true` puede ser null.
 */
export function mountChrome(
  options: MountChromeOptions & { allowAnonymous?: false | undefined },
): Promise<MountChromeResultAuthed>;
export function mountChrome(
  options: MountChromeOptions & { allowAnonymous: true },
): Promise<MountChromeResult>;
export async function mountChrome(
  options: MountChromeOptions,
): Promise<MountChromeResult> {
  const meResult = await authApi.me();
  let user: AuthenticatedPilot | null = null;

  if (meResult.ok) {
    user = meResult.data.user;
  } else if (!options.allowAnonymous) {
    const back = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    window.location.replace(`/login.html?return=${back}`);
    // Promesa que nunca resuelve: el caller no debe continuar tras un redirect.
    return new Promise<MountChromeResult>(() => {});
  }

  document.body.classList.add('chrome-mode');
  document.body.innerHTML = renderChrome(user, options.activeSection);

  if (user) attachLogoutHandler();
  blockComingSoonClicks();

  const contentRoot = document.querySelector<HTMLElement>('.chrome-main');
  if (!contentRoot) {
    throw new Error('[chrome] no se encontró .chrome-main tras el render');
  }

  return { contentRoot, user };
}

/** Conecta el ítem SALIR: POST /api/auth/logout → redirect a /. */
function attachLogoutHandler(): void {
  const logoutBtn = document.querySelector<HTMLAnchorElement>('[data-action="logout"]');
  if (!logoutBtn) return;
  logoutBtn.addEventListener('click', async (ev) => {
    ev.preventDefault();
    await authApi.logout();
    window.location.href = '/';
  });
}

/** Cancela navegación de los ítems atenuados. */
function blockComingSoonClicks(): void {
  const items = document.querySelectorAll<HTMLAnchorElement>('.chrome-menu-item--coming-soon');
  items.forEach((a) => {
    a.addEventListener('click', (ev) => ev.preventDefault());
  });
}
